/* ============================================================
   Sacrasoul mobile drawer — THE PROCESSION (Lane B)
   Deferred, no build step. Owns only: open/close, the doorway->
   panel shared-element FLIP, the inline size disclosure, the
   left-edge swipe-back, and the visual "added" state.

   The CART is NOT reimplemented here. Every buy control is a real
   <form action="/cart/add"> submit button; the site-wide interceptor
   in sacrasoul-cart-drawer.js catches the submit, adds the line,
   opens the cart drawer and bumps the badge. With JS off, the same
   form posts natively to /cart/add. This file only lights the chosen
   chip and drives motion.
   ============================================================ */
(function () {
  'use strict';

  const root = document.querySelector('.ssd-root');
  if (!root) return;

  const body = document.body;

  // Portal the drawer to <body>: it renders inside the header section, and the
  // #PageRecede transform would otherwise turn its fixed positioning into
  // ancestor-relative positioning the moment the page recedes.
  if (root.parentElement !== body) body.appendChild(root);
  const drawer = root.querySelector('.ssd-drawer');
  const level1 = root.querySelector('.ssd-level1');
  const scrim = root.querySelector('.ssd-scrim');
  const procession = root.querySelector('.ssd-procession');
  const closeBtn = root.querySelector('.ssd-close');
  const toggle = document.getElementById('mobileMenuToggle');
  const panelMount = root.querySelector('[data-ssd-panel-mount]');
  let panels = Array.prototype.slice.call(root.querySelectorAll('.ssd-panel'));
  const reduced = window.matchMedia('(prefers-reduced-motion:reduce)').matches;

  // Progressive enhancement flag: with .js present, size bands collapse by
  // default and the disclosure chip drives them. Without it (no JS) every
  // size button stays visible and submits natively.
  root.classList.add('js');

  /* Bound on the anti-FOUC gate in snippets/header-drawer.liquid. That inline
     floor holds .ssd-stack hidden until assets/sacrasoul-drawer.css applies
     (its own .ssd-stack rule is the release), so a tap that beats the
     print-swapped skin cannot open a sheet of unstyled markup (Sho, 2026-09-15).
     A stylesheet that 404s or hangs must not leave the menu permanently empty:
     four seconds after the first open, release the gate ourselves. Long enough
     that a merely slow sheet still wins; short enough that a dead one does not
     cost the menu. `inherit`, not `visible`, so the stack keeps following the
     closed drawer's hidden state afterwards (keyboard containment at rest).
     Nothing is read here: no style work lands on the tap. */
  let skinBound = false;
  function ensureSkin() {
    if (skinBound) return;
    skinBound = true;
    const stack = root.querySelector('.ssd-stack');
    if (stack) setTimeout(function () { stack.style.visibility = 'inherit'; }, 4000);
  }

  let openPanelEl = null;
  let openPanelTrigger = null;
  let warmed = false;

  /* The deep category panels are server-rendered into an inert raw-text source.
     Parse them only when the menu is first opened, so every non-menu page view
     avoids constructing hundreds of hidden product-row and size-chip nodes. */
  function hydratePanels() {
    // A chunked pass may be part-way through: take the rest of it in one go so
    // every caller below sees a COMPLETE panel list, never a partial one.
    flushPendingPanels();
    if (panels.length) return panels;
    /* Only the section's own deferred-payload container may supply the raw
       source — a document-wide lookup would let any earlier matching element
       steer what gets parsed into the header mount. Root fallback covers the
       legacy in-drawer markup. */
    const source = document.querySelector('[data-ss-deferred-header-payloads] [data-ssd-panel-source]')
      || root.querySelector('[data-ssd-panel-source]');
    if (!source) {
      // Source missing or already consumed: re-query so a server-rendered
      // fallback or a previous partial parse is still discovered. Bind the
      // back buttons here too — server-rendered fallback panels arrive
      // without listeners, and init no longer binds them at load.
      const fallbackPanels = document.querySelectorAll('[data-ss-deferred-header-payloads] .ssd-panel');
      if (panelMount) fallbackPanels.forEach(function (panel) { panelMount.appendChild(panel); });
      panels = Array.prototype.slice.call(root.querySelectorAll('.ssd-panel'));
      panels.forEach(function (panel) {
        if (panel.dataset.ssdBackBound) return;
        panel.dataset.ssdBackBound = '1';
        const back = panel.querySelector('.ssd-back');
        if (back) back.addEventListener('click', stepBack);
      });
      return panels;
    }

    try {
      const staging = document.createElement('template');
      staging.innerHTML = source.textContent.trim();
      const found = staging.content.querySelectorAll('.ssd-panel').length;
      if (!found) return panels; // keep the source for a retry; do not consume it
      (panelMount || source.parentNode).appendChild(staging.content);
      source.remove();
    } catch (err) {
      // Parse failed (e.g. Trusted Types enforcement). Keep the source intact so
      // a later attempt can retry; callers fall back to native link navigation.
      return panels;
    }
    panels = Array.prototype.slice.call(root.querySelectorAll('.ssd-panel'));
    panels.forEach(function (panel) {
      const back = panel.querySelector('.ssd-back');
      if (back) back.addEventListener('click', stepBack);
    });
    return panels;
  }

  /* Hydration is the most expensive thing this drawer does. MEASURED on the live
     EN homepage markup (28,858-char payload, 5 panels, 607 tags) in headless
     Chromium at 6x CPU throttle, tapping #mobileMenuToggle:

       synchronous in the click handler   handler 119-210ms, tap->paint 208-312ms
       one deferred task after the paint  handler  35-48ms, tap->paint  96-120ms
                                          ...but a 91-153ms task right behind it
       chunked, one panel per task        see tests/browser/drawer-inp-proof.mjs

     Two things follow from those numbers, and both matter more than the guess
     they replace. First: this parse is worth moving off the input handler — 208-312ms
     tap->paint is over the 200ms INP "good" bar on its own. Second: it is NOT the
     ~970ms the field p75 reported at n=7, so moving it does not by itself explain
     or close that finding; see the PR body.

     So: Level 1 (the procession) is server-rendered and needs none of this, so open
     first, then hydrate. rAF alone would not defer it — an rAF callback runs before
     the frame it belongs to is presented, so the parse would still block that paint;
     rAF -> setTimeout(0) lands after it. And deferring alone only MOVES the long
     task into the drawer's slide, where it can drop the open animation's frames and
     delay whatever the shopper taps next. So the work is also split: parse once into
     a detached template, then insert ONE panel per task. Each chunk is short enough
     that a doorway tap landing mid-hydration is not stuck behind the whole parse. */
  var pendingPanels = null;   // parsed, not yet inserted — drained one per task
  var pendingMount = null;    // original drawer mount (legacy fixtures use source parent)
  var pendingSource = null;   // raw source after <main>, removed when the queue drains
  var chunkTimer = 0;

  function adoptPanel(el) {
    pendingMount.appendChild(el);
    el.dataset.ssdBackBound = '1';
    const back = el.querySelector('.ssd-back');
    if (back) back.addEventListener('click', stepBack);
    panels.push(el);
  }

  /* Insert the rest NOW. Called by hydratePanels(), i.e. by every consumer that
     needs the whole list (a doorway tap, openPanel, the idle warm). Worst case
     this costs the remaining insertions — never the parse, which already happened. */
  function flushPendingPanels() {
    if (chunkTimer) { clearTimeout(chunkTimer); chunkTimer = 0; }
    if (!pendingPanels) return;
    while (pendingPanels.length) adoptPanel(pendingPanels.shift());
    pendingPanels = null;
    if (pendingSource) { pendingSource.remove(); pendingSource = null; }
    pendingMount = null;
  }

  function insertNextPanel() {
    chunkTimer = 0;
    if (!pendingPanels) return;
    if (pendingPanels.length) adoptPanel(pendingPanels.shift());
    if (pendingPanels.length) { chunkTimer = setTimeout(insertNextPanel, 0); return; }
    flushPendingPanels();
  }

  function beginChunkedHydration() {
    if (panels.length || pendingPanels) return;
    // Same scoping rule as hydratePanels above: the trusted container first,
    // the drawer root for legacy markup, never the whole document.
    const source = document.querySelector('[data-ss-deferred-header-payloads] [data-ssd-panel-source]')
      || root.querySelector('[data-ssd-panel-source]');
    // No raw-text source: server-rendered fallback (the mixed-case "</script"
    // branch in header-drawer.liquid). Nothing to parse — take the normal path.
    if (!source) { hydratePanels(); return; }
    let staging;
    try {
      staging = document.createElement('template');
      staging.innerHTML = source.textContent.trim();
    } catch (err) {
      return;   // Trusted Types etc. — keep the source intact for a synchronous retry
    }
    const found = staging.content.querySelectorAll('.ssd-panel');
    if (!found.length) return;   // keep the source; do not consume it
    pendingPanels = Array.prototype.slice.call(found);
    pendingMount = panelMount || source.parentNode;
    pendingSource = source;
    insertNextPanel();
  }

  /* Run `fn` in a task that starts AFTER the next frame is painted. rAF alone is
     not enough: its callbacks run at the top of a rendering opportunity, before
     style, layout and paint, so work parked there still lands inside the frame
     the shopper is waiting on. The setTimeout inside it is what pushes past the
     paint. Without rAF (no browser has lacked it since 2013, but a hostile
     polyfill environment might) fall back to a plain short timer. */
  function afterPaint(fn) {
    if (typeof requestAnimationFrame !== 'function') { setTimeout(fn, 32); return; }
    requestAnimationFrame(function () { setTimeout(fn, 0); });
  }

  function hydratePanelsAfterPaint() {
    if (panels.length || pendingPanels) return;
    afterPaint(beginChunkedHydration);
  }

  /* A doorway tap that beats the chunk queue needs exactly ONE panel — its own.
     Adopt that and leave the rest draining, so the tap pays a single insert
     (measured 6-36ms at 6x throttle) instead of the whole remaining queue (~86ms).
     Returns whether a panel for `cat` is now in the DOM; false means the doorway's
     real href navigates, so a category link is never a dead tap. */
  function hydrateFor(cat) {
    function has(list) {
      return list.some(function (p) { return p.getAttribute('data-cat') === cat; });
    }
    if (has(panels)) return true;
    if (pendingPanels) {
      for (var i = 0; i < pendingPanels.length; i++) {
        if (pendingPanels[i].getAttribute('data-cat') === cat) {
          adoptPanel(pendingPanels.splice(i, 1)[0]);
          return true;
        }
      }
    }
    // Not parsed yet, or a previous parse failed: fall back to the full
    // synchronous path, which is also the server-rendered-fallback path.
    return has(hydratePanels());
  }

  /* ---------- open / close ---------- */
  // With a category panel open, Level 1 is only dimmed and pushed 28% aside
  // (.is-pushed in sacrasoul-drawer.css) — it stays focusable, so Tab walked out
  // of the panel into the search field and doorway links sitting behind it at
  // 55% opacity, and screen readers read both layers as one page. The drawer's
  // aria-modal="true" only ever contained the page, never its own two levels.
  function holdLevel1() {
    if (!level1) return;
    level1.setAttribute('inert', '');
    level1.setAttribute('aria-hidden', 'true');
  }
  function releaseLevel1() {
    if (!level1) return;
    level1.removeAttribute('inert');
    level1.removeAttribute('aria-hidden');
  }

  function stepBack(event) {
    const returnFocus = event && event.type === 'click';
    const trigger = openPanelTrigger;
    if (openPanelEl) {
      openPanelEl.classList.remove('open', 'noslide');
      openPanelEl.setAttribute('aria-hidden', 'true');
      openPanelEl = null;
    }
    openPanelTrigger = null;
    clearTimeout(focusTimer);
    releaseLevel1();
    root.classList.remove('is-pushed');
    level1.style.transform = '';
    level1.style.transition = '';
    if (returnFocus && trigger && trigger.isConnected) {
      setTimeout(function () {
        try { trigger.focus({ preventScroll: true }); }
        catch (e) { trigger.focus(); }
      }, 0);
    }
  }

  // The doorway animation is `.ssd-root.is-open .ssd-procession .ssd-door` in
  // sacrasoul-drawer.css, and closeDrawer() takes `.is-open` off the root. So
  // every open adds `animation: ssdDoorIn` through a selector that did not match
  // a moment ago, and the style engine starts it at zero on its own. The
  // animation:none -> forced reflow -> animation:'' triple below it was the
  // usual restart trick applied to an animation that never needed restarting.
  //
  // It was not free either. openDrawer() calls this AFTER the writes that put
  // <body> into position:fixed, add .is-open, and mark #PageRecede inert — so the
  // offsetWidth read forced a full-document style recalc plus layout, inside the
  // tap, on a document those same writes had just dirtied end to end. It is the
  // one forced read left on the tap's own path, and the only one this function
  // ever needed was for a restart it does not need.
  //
  // Deleting it does not make the invalidation free — whatever reads style next
  // in the same click dispatch still pays it, and on the live store that is
  // PostHog autocapture. It removes one full recalc from the tap, not the last
  // one. See the note in openDrawer: this function is simply not the place to
  // force a measurement nothing here uses.
  //
  // What is left is the part that does work: the per-door stagger delay, and the
  // hairline that draws itself in behind each doorway.
  function staggerDoors() {
    const doors = Array.prototype.slice.call(procession.querySelectorAll('.ssd-door'));
    doors.forEach(function (d, i) {
      d.classList.remove('hair-in');
      d.style.animationDelay = (0.04 * i) + 's';
      setTimeout(function () { d.classList.add('hair-in'); }, 260 + i * 40);
    });
  }

  // The body lock (position:fixed) would snap the page to the top and ruin the
  // recede — pin the current scroll into `top` and give it back on close.
  let lockY = 0;

  /* Drawer product/doorway images carry data-src and only load when the drawer is
     first opened, so a closed menu never spends bandwidth on its images at page load. */
  /* The idle warm below sets this while it walks the hidden panels. The four
     .ssd-kcard kit cards are 360w and were 124KB of the 176KB warm — two thirds
     of it for one of five categories. They load when their panel opens (the
     direct loadCategoryImages(panel) call in openPanel runs with the flag off). */
  var skipKitCards = false;

  function loadCategoryImages(content) {
    if (!content) return;
    var imgs = content.querySelectorAll('img[data-src]');
    for (var i = 0; i < imgs.length; i++) {
      if (skipKitCards && imgs[i].closest && imgs[i].closest('.ssd-kcard')) continue;
      if (imgs[i].getAttribute('data-srcset')) {
        imgs[i].setAttribute('srcset', imgs[i].getAttribute('data-srcset'));
        imgs[i].removeAttribute('data-srcset');
      }
      imgs[i].setAttribute('src', imgs[i].getAttribute('data-src'));
      imgs[i].removeAttribute('data-src');
    }
  }

  /* Data Saver on, or a 2G-class link: the panels can wait for their own tap. */
  function saveDataOn() {
    var c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return false;
    return c.saveData === true || /(^|-)2g$/.test(c.effectiveType || '');
  }

  /* Resolve once every <img> under `scope` has loaded or failed, or after `cap`ms,
     whichever comes first. decode() is used where available so the callback waits
     for a usable frame rather than just the bytes. */
  function whenImagesSettled(scope, cap, cb) {
    var done = false;
    function finish() { if (done) return; done = true; clearTimeout(timer); cb(); }
    var timer = setTimeout(finish, cap);
    var imgs = scope ? Array.prototype.slice.call(scope.querySelectorAll('img')) : [];
    var pending = 0;
    function one() { if (--pending <= 0) finish(); }
    imgs.forEach(function (img) {
      if (img.complete) {
        if (!img.decode) return;
        pending++;
        img.decode().then(one, one);
        return;
      }
      pending++;
      img.addEventListener('load', one, { once: true });
      img.addEventListener('error', one, { once: true });
    });
    if (!pending) finish();
  }

  /* Keyboard containment, held without touching style.

     `inert` is the real modal boundary, but applying it costs a document-wide
     style invalidation that we do not want inside the tap (see openDrawer). This
     covers the gap until it lands. One capture-phase focusin on document: if
     focus surfaces anywhere outside the drawer while the drawer is open, it is
     taken off there at once. Capture phase so it runs before anything
     the page has bound, and `focusin` rather than a Tab keydown because it also
     catches shift-Tab, browser find, AT focus moves and programmatic focus() —
     every way focus can land somewhere it should not, not just the one keystroke.

     Installing it costs one addEventListener. It reads no style and no layout.
     It stays installed for the life of the open drawer, which also makes it a
     backstop for the window BEFORE closeBtn.focus() lands at 120ms.

     It BLURS before it redirects, and the order matters. In the tap's own task
     the sheet is still visibility:hidden — `.ssd-drawer` transitions visibility
     (sacrasoul-drawer.css), and at the first recalc after `.is-open` the computed
     value is still the from-value — so nothing inside the drawer is focusable
     yet and closeBtn.focus() is a no-op that leaves focus sitting on the page
     behind. Blurring always works. Focus parks on <body>, which is not tabbable
     and is not behind the scrim; the next Tab that tries to land in the page
     re-enters this guard and is blurred again, so focus can never come to rest
     out there. Once the sheet is visible the redirect takes over and focus goes
     where it should. */
  var focusGuard = null;
  var focusTimer = 0;
  function trapFocus() {
    if (focusGuard) return;
    focusGuard = function (e) {
      if (!root.classList.contains('is-open')) return;
      // Police ONLY the receded page — the same subtree `inert` will cover.
      // "Everything outside the drawer" is the wrong boundary: the cart
      // drawer, popups and this menu itself are all portaled to <body>
      // beside .ssd-root, outside #PageRecede, and a guard that blurs them
      // makes the cart drawer keyboard-unreachable whenever it opens over
      // the menu. inert never touches those surfaces; neither may this.
      var shell = document.getElementById('PageRecede');
      if (!shell || !shell.contains(e.target)) return;
      if (e.target && e.target.blur) e.target.blur();
      if (closeBtn) closeBtn.focus();
      else if (drawer && drawer.focus) drawer.focus();
    };
    document.addEventListener('focusin', focusGuard, true);
    // The element already focused at open time — usually the hamburger toggle,
    // inside #PageRecede — never re-fires focusin, so the listener alone leaves
    // it holding focus behind the exposed dialog until inert lands. Contain it
    // now: blur parks focus on <body> (not tabbable, and the guard catches any
    // re-entry). activeElement is a DOM read, not a style or layout read.
    var shellNow = document.getElementById('PageRecede');
    var ae = document.activeElement;
    if (shellNow && ae && shellNow.contains(ae) && ae.blur) ae.blur();
  }
  function releaseFocus() {
    if (!focusGuard) return;
    document.removeEventListener('focusin', focusGuard, true);
    focusGuard = null;
  }

  /* Apply `inert` past the paint, but never later than the bound.

     afterPaint() is the fast path and the one that runs in a foreground tab. The
     timer is not a duplicate of it: rAF does not fire at all while a tab is
     hidden or occluded, so on that path afterPaint alone would leave the modal
     boundary unapplied for as long as the shopper is away. Whichever arrives
     first wins; the closure's `fired` flag makes the loser a no-op.

     Both paths re-check `is-open`. A deferred apply that lands after the drawer
     has already closed would leave the entire page inert with a reload the only
     way out — the exact failure closeDrawer's own comment records from the last
     time it shipped. */
  /* inertGen is MONOTONIC — it only counts up. A reset-to-zero cancellation
     collides: open A takes 1, close resets 0, open B takes 1 again, and A's
     stale 100ms timer passes B's token check and applies inert before B's
     first paint — the exact cost this deferral exists to avoid. Counting up
     makes every open and every close a new generation no stale callback can
     match. `fired` keeps the rAF/timer race's loser a no-op. */
  var inertGen = 0;
  function applyInertSoon(shell) {
    var token = ++inertGen;
    var fired = false;
    var apply = function () {
      if (fired || token !== inertGen) return;     // superseded or cancelled
      fired = true;
      if (root.classList.contains('is-open')) shell.setAttribute('inert', '');
    };
    afterPaint(apply);
    setTimeout(apply, 100);
  }

  function openDrawer() {
    ensureSkin();
    hydratePanelsAfterPaint();
    /* READS FIRST, then writes. Everything below this block invalidates style or
       layout for the whole document — body goes position:fixed, #PageRecede goes
       inert, .is-open lands on the root — so a read placed after any of them
       forces a full recalc of a document that was just dirtied end to end, and
       the shopper waits for it between the tap and the sheet's first frame.
       Read while the document is still clean and the reads are nearly free.

       `window.innerHeight` used to sit on the transformOrigin line below, after
       body.style.top had already dirtied layout. Blink happens not to force
       layout for innerHeight today, which is why that ordering never showed up
       as a cost — but it is one edit away from being a real read-after-write,
       and the ordering is the contract, not the current engine's shortcut.
       tests/test_storefront_performance_contracts.py pins it. */
    const shell = document.getElementById('PageRecede');
    lockY = window.scrollY;
    // Recede about a point 6% down the VISIBLE viewport (the demo's 50% 6%).
    // The shell is the whole document, so the origin must track the lock scroll.
    const originY = lockY + window.innerHeight * 0.06;

    body.style.top = -lockY + 'px';
    if (shell) shell.style.transformOrigin = '50% ' + originY + 'px';
    // Enforce the aria-modal contract: the receded page (header + main) goes inert while
    // the drawer is open, so Tab and assistive tech stay inside the sheet and never reach
    // content behind the scrim. The drawer is portaled to <body>, outside #PageRecede.
    //
    // `inert` on #PageRecede is the whole page, so the write invalidates style
    // for every node under it and the next thing in the click dispatch to read
    // style or layout pays the recalc. Measured on this repo's live-sized
    // fixture (2,374 nodes, base.css, a document-level listener standing in for
    // PostHog autocapture, 10x CPU): the tap's click processing is 80.6ms with
    // this write inside the tap and 23.0ms with it after the frame. That is the
    // single largest thing openDrawer does to the interaction.
    //
    // But `inert` is not decoration — it is what makes this a modal, and it
    // cannot simply be deferred. Two things have to hold from the instant the
    // dialog is exposed: keyboard focus must not escape, and assistive tech must
    // not reach the page behind. So they are held by two different mechanisms
    // with two different costs:
    //
    //   1. Focus containment, SYNCHRONOUS. trapFocus() below installs one
    //      capture-phase focusin listener on document. It is O(1) — it adds no
    //      style invalidation and nothing to the recalc — and it closes the Tab
    //      escape completely, from this line onward, with no dependency on any
    //      timer or frame.
    //   2. The inert subtree itself, AFTER the frame — and on a bound. rAF alone
    //      would not do: in a hidden or occluded tab rAF is paused indefinitely,
    //      which is precisely the case where "one frame" becomes "until the
    //      shopper comes back". applyInertSoon() races the post-paint path
    //      against a plain timer, so whichever the browser is still running
    //      wins. In a foreground tab that is the frame (~16ms); in a hidden one
    //      the timer, throttled to about a second — a tab nobody is interacting
    //      with, with focus already contained by (1).
    if (shell) { trapFocus(); applyInertSoon(shell); }
    root.classList.remove('is-closing');
    root.classList.add('is-open');
    body.classList.add('mobile-menu-open');
    drawer.removeAttribute('inert');
    drawer.setAttribute('aria-hidden', 'false');
    if (toggle) toggle.setAttribute('aria-expanded', 'true');
    staggerDoors();
    /* Only the procession is on screen. This used to pass the whole drawer, so
       first open fired ~24 image requests — 5 doorways plus ~19 behind a tap the
       shopper may never make. On HTTP/2 they all multiplex, so the five visible
       doorways got about a fifth of the pipe and the menu looked unfinished for
       roughly 850ms longer than it needed to on 4G. A category's images now load
       when the shopper walks through its doorway, which is what the desktop mega
       already does per panel. It also ran BEFORE .is-open, scheduling all those
       fetches ahead of the sheet's first frame. */
    var content = level1 || drawer;
    loadCategoryImages(content);
    /* Then warm the panels while the device is idle. Loading everything up front
       (the old behaviour) starved the five visible doorways of bandwidth;
       loading a panel only on its doorway tap traded that for a wait inside the
       panel. Idle-warming gives the doorways the whole pipe first and still has
       the panels ready by the time a shopper picks one. openPanel() also calls
       loadCategoryImages directly, so a fast tap is never left waiting on idle. */
    if (!warmed) {
      warmed = true;
      /* The warm needs the full panel list, but calling hydratePanels() here would
         FLUSH the chunk queue — collapsing five short tasks back into one ~86ms
         one, which is the exact long task this deferral exists to avoid. So wait
         for the queue to drain on its own; the bound stops a stalled queue from
         parking the warm forever, and hydratePanels() then covers the case where
         hydration never started at all (no source, or a failed parse). */
      var warmTries = 0;
      var warm = function () {
        if (pendingPanels && warmTries++ < 40) { setTimeout(warm, 32); return; }
        hydratePanels();
        skipKitCards = true;
        panels.forEach(loadCategoryImages);
        skipKitCards = false;
      };
      var schedule = function () {
        if (window.requestIdleCallback) requestIdleCallback(warm, { timeout: 2500 });
        else setTimeout(warm, 900);
      };
      /* requestIdleCallback tracks the MAIN THREAD, not the network: measured on a
         phone the 18 hidden-panel requests started 23ms after the doorway requests
         and before any doorway had finished, so the warm (larger than the visible
         strip) multiplexed against it on the same H2 connection. Wait for the
         doorways to actually settle first, capped so a stalled image can't park the
         warm forever. And on a metered or 2G connection, don't warm at all. */
      if (!saveDataOn()) whenImagesSettled(content, 1500, schedule);
    }
    if (closeBtn) { clearTimeout(focusTimer); focusTimer = setTimeout(function () { closeBtn.focus(); }, 120); }
  }

  var closingTimer = 0;
  function closeDrawer() {
    if (!root.classList.contains('is-open')) return;
    root.classList.add('is-closing');
    root.classList.remove('is-open');
    body.classList.remove('mobile-menu-open');
    body.style.top = '';
    // Lift inert FIRST. It used to sit after the scrollTo below, and
    // `behavior:'instant'` only entered the ScrollBehavior enum in Safari 15.4 /
    // Chrome 97 — an unrecognised enum member in a dictionary throws TypeError,
    // so on an older iOS the sheet slid away and every line after it was
    // skipped: #PageRecede stayed inert and the entire page (header, main,
    // footer) was permanently non-interactive with a reload the only way out.
    var shell = document.getElementById('PageRecede');
    if (shell) shell.removeAttribute('inert');
    // Cancel any inert apply still in flight and lift the focus guard. Both run
    // AFTER removeAttribute above, so an apply that fires between the two lines
    // still finds `is-open` gone and does nothing. Bumping inertGen makes that
    // belt-and-braces — a monotonic generation no stale callback can match —
    // and the 120ms focus timer must die here too, or a same-task open/close
    // has its focus stolen into the aria-hidden drawer at 120ms. The guard has
    // to come off or a keyboard user who closed the drawer would be pulled
    // back to a close button that is gone.
    inertGen++;
    clearTimeout(focusTimer);
    releaseFocus();
    drawer.setAttribute('inert', '');
    drawer.setAttribute('aria-hidden', 'true');
    // instant, not smooth — a smooth restore races the un-fixing reflow and gets cancelled
    try { window.scrollTo({ top: lockY, left: 0, behavior: 'instant' }); }
    catch (e) { window.scrollTo(0, lockY); }
    if (toggle) { toggle.setAttribute('aria-expanded', 'false'); toggle.focus({ preventScroll: true }); }
    stepBack(); // reopen starts on Level 1
    /* One pending close at a time: an open-close-open-close inside 460 ms used to let
       the first timer lift is-closing while the second slide was still running. */
    clearTimeout(closingTimer);
    closingTimer = setTimeout(function () { root.classList.remove('is-closing'); }, 460);
  }

  if (toggle) toggle.addEventListener('click', function () {
    if (root.classList.contains('is-open')) closeDrawer(); else openDrawer();
  });
  if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
  if (scrim) scrim.addEventListener('click', closeDrawer);

  /* ---- Warm the five doorways once the PAGE is done, not on the tap. ----
     The procession is the only thing on screen when the sheet opens, and its
     five images are the one part of the menu already sitting in the static DOM
     at page load (everything below Level 1 arrives with the panel payload).
     Left to openDrawer() they start from zero at the tap: measured on the
     preview at 390px, Fast 3G, 4x CPU, the first doorway byte left at the tap
     and the five finished 0.8s-1.6s later, so the menu opened onto five empty
     frames every time. 160.8KB, all five already served as image/webp.

     This does not put those bytes on the critical path. It waits for `load`
     (the page's own images are finished), then for a settle beat, then for an
     idle main thread -- the same requestIdleCallback the panel warm uses. The
     `loading="lazy"` on each doorway still applies after the promotion, so an
     off-screen doorway costs nothing until it is near the viewport.

     Three gates, all of which mean "these bytes would be waste":
       - saveData / 2G-class link  (same rule as the panel warm below)
       - a viewport wide enough that the hamburger is hidden: header-drawer.liquid
         only loads this script on a phone-width viewport, but the drawer menu
         type loads it at every width, a phone may rotate or widen after load,
         and there the sheet is unreachable
       - the sheet already opened, which promotes them anyway
     Kept armed on change, so a rotation or a narrowing window still warms. */
  var doorsWarmed = false;
  var phone = window.matchMedia('(max-width: 768px)');
  function warmDoorways() {
    if (doorsWarmed || !phone.matches || saveDataOn()) return;
    doorsWarmed = true;
    loadCategoryImages(level1);
  }
  function scheduleDoorwayWarm() {
    if (doorsWarmed || !phone.matches || saveDataOn()) return;
    var idle = function () {
      if (window.requestIdleCallback) requestIdleCallback(warmDoorways, { timeout: 3000 });
      else setTimeout(warmDoorways, 1200);
    };
    var settle = function () { setTimeout(idle, 600); };
    if (document.readyState === 'complete') settle();
    else window.addEventListener('load', settle, { once: true });
  }
  scheduleDoorwayWarm();

  /* ---- Lay the closed sheet out once the page has painted (2026-09-23). ----
     snippets/header-drawer.liquid keeps .ssd-stack at content-visibility:hidden
     until .ssd-ready, so the sheet's contents cost nothing while the hero is
     being painted. Here they are released on the first idle moment after
     `load` (+ a settle beat, as the doorway warm does), so the layout happens
     while nobody is waiting and the first tap finds the sheet ready. Every
     viewport, every connection: this is layout, not bytes. */
  (function () {
    var release = function () { root.classList.add('ssd-ready'); };
    var idle = function () {
      if (window.requestIdleCallback) requestIdleCallback(release, { timeout: 2000 });
      else setTimeout(release, 300);
    };
    var settle = function () { setTimeout(idle, 300); };
    if (document.readyState === 'complete') settle();
    else window.addEventListener('load', settle, { once: true });
  })();
  if (phone.addEventListener) phone.addEventListener('change', scheduleDoorwayWarm);
  else if (phone.addListener) phone.addListener(scheduleDoorwayWarm);

  /* The navigation-commit handler below lives in its own closure and needs
     to close the sheet for an in-page link (see "same document" there). */
  root.__ssdClose = closeDrawer;
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' && root.classList.contains('is-open')) closeDrawer();
  });

  // Rotating/resizing up to the desktop breakpoint while the drawer is open would strand
  // the sheet over the desktop layout: the CSS hides only the hamburger, and the drawer is
  // portaled to <body>, out of that rule's scope. Close it when we cross to desktop.
  var desktopMQ = window.matchMedia('(min-width: 769px)');
  desktopMQ.addEventListener('change', function () {
    if (desktopMQ.matches && root.classList.contains('is-open')) closeDrawer();
  });

  /* ---------- Level 1 -> Level 2 : walk THROUGH the doorway ---------- */
  function openPanel(key, doorEl) {
    const sel = '.ssd-panel[data-cat="' + (window.CSS && CSS.escape ? CSS.escape(key) : key) + '"]';
    // Usually already in the DOM — the chunk queue or hydrateFor() put it there,
    // and then this tap owes nothing. Only pay for hydration if it is truly absent.
    let panel = root.querySelector(sel);
    if (!panel) { hydratePanels(); panel = root.querySelector(sel); }
    if (!panel) return;

    /* Bind the back button HERE, on the panel actually being opened, not only in
       the hydration passes. This query found the panel in the DOM directly --
       it never consults the panels[] list -- so a panel that arrived by any path
       the passes missed (hydratePanels' early return when panels[] is already
       non-empty walks past a direct-DOM panel) opened fine and then its back
       button did nothing: Level 1 stayed inert behind it and the only way out
       was closing the whole drawer. The browser proof caught it the first day
       it could run (drawer-a11y-proof, 2026-08-02). Idempotent via the flag. */
    if (!panel.dataset.ssdBackBound) {
      panel.dataset.ssdBackBound = '1';
      const lateBack = panel.querySelector('.ssd-back');
      if (lateBack) lateBack.addEventListener('click', stepBack);
    }

    // reset any other open panel instantly
    panels.forEach(function (p) {
      if (p !== panel) { p.classList.remove('open', 'noslide'); p.setAttribute('aria-hidden', 'true'); }
    });

    const doorImg = doorEl && doorEl.querySelector('img');
    const sRect = doorImg ? doorImg.getBoundingClientRect() : null;
    const heroImg = panel.querySelector('.ssd-panel-hero img');
    const list = panel.querySelector('.ssd-panel-list');

    // present without a horizontal slide — the hero FLIP carries it in
    panel.classList.add('noslide', 'open');
    panel.setAttribute('aria-hidden', 'false');
    void panel.offsetWidth;            // commit the instant transform
    panel.classList.remove('noslide'); // restore the transition for the exit slide
    root.classList.add('is-pushed');
    openPanelEl = panel;
    openPanelTrigger = doorEl || null;
    loadCategoryImages(panel);
    holdLevel1();
    if (list) list.scrollTop = 0;
    /* Entering a second-level view makes the focused doorway inert. Hand
       keyboard and AT focus to the new view instead of letting the browser
       drop it on <body>. This also cancels openDrawer's delayed close-button
       focus, which otherwise steals focus back out of a quickly opened panel. */
    clearTimeout(focusTimer);
    const panelBack = panel.querySelector('.ssd-back');
    if (panelBack) {
      focusTimer = setTimeout(function () {
        if (openPanelEl !== panel || !root.classList.contains('is-open')) return;
        try { panelBack.focus({ preventScroll: true }); }
        catch (e) { panelBack.focus(); }
      }, reduced ? 0 : 80);
    }

    // Shared-element FLIP: the pressed doorway image flies into the hero band.
    // Only when the hero already has real pixels. On the FIRST cold tap the
    // hero was promoted from data-src a moment ago and is still in flight:
    // a flyer pointing at that URL started a SECOND request for it (the
    // browser proof's duplicate: 5 requests for a 4-image panel) and flew a
    // BLANK box while both loaded. A cold tap now presents the panel plainly
    // and spends its bandwidth on the images; the warm tap -- the common one,
    // the idle warm has usually finished before a shopper picks a category --
    // keeps the animation. No new network request is ever the price of a
    // transition.
    const heroReady = !!heroImg && heroImg.complete && heroImg.naturalWidth > 0;
    if (!reduced && sRect && heroImg && heroReady) {
      const hero = panel.querySelector('.ssd-panel-hero');
      const hRect = hero.getBoundingClientRect();
      heroImg.style.opacity = '0';
      const fly = document.createElement('img');
      fly.className = 'ssd-flyer';
      // The flyer must show pixels for the whole 540ms flight. The hero is the
      // right destination image but on a cold tap it has only just been asked
      // for, and an img with a pending src paints nothing — the shopper watched
      // an empty box fly across. The doorway they pressed is on screen, so it
      // is decoded by definition; fall back to it until the hero is ready.
      const heroReady = heroImg.complete && heroImg.naturalWidth > 0;
      fly.src = (!heroReady && doorImg && (doorImg.currentSrc || doorImg.src)) ||
                heroImg.currentSrc || heroImg.src;
      fly.alt = '';
      fly.style.left = hRect.left + 'px';
      fly.style.top = hRect.top + 'px';
      fly.style.width = hRect.width + 'px';
      fly.style.height = hRect.height + 'px';
      fly.style.transformOrigin = 'top left';
      const sx = sRect.width / hRect.width, sy = sRect.height / hRect.height;
      fly.style.transform = 'translate(' + (sRect.left - hRect.left) + 'px,' + (sRect.top - hRect.top) + 'px) scale(' + sx + ',' + sy + ')';
      document.body.appendChild(fly);
      requestAnimationFrame(function () {
        fly.style.transition = 'transform .54s cubic-bezier(0.16,1,0.3,1)';
        fly.style.transform = 'translate(0,0) scale(1,1)';
      });
      let settled = false;
      const settle = function () { if (settled) return; settled = true; heroImg.style.opacity = ''; fly.remove(); };
      fly.addEventListener('transitionend', settle, { once: true });
      setTimeout(settle, 720);         // safety net
    } else if (heroImg) {
      heroImg.style.opacity = '';
    }
  }

  if (procession) procession.addEventListener('click', function (e) {
    const d = e.target.closest('.ssd-door');
    if (!d) return;
    // Only claim the click if the requested panel actually exists after hydration.
    // If hydration failed or produced nothing, let the door's real href navigate:
    // a category link must never become a dead tap.
    const cat = d.getAttribute('data-cat');
    const available = hydrateFor(cat);
    if (!available) {
      /* The raw source is fetched on intent (assets/ss-header-payloads.js). A tap
         that beats the fetch waits for it and walks through the doorway once;
         only a fetch that has failed lets the real href navigate. */
      const hp = window.SacrasoulHeaderPayloads;
      if (hp && !hp.done) {
        e.preventDefault();
        (hp.ready || hp.load()).then(function (ok) {
          if (ok && hydrateFor(cat)) openPanel(cat, d);
          else if (!ok) window.location.href = d.href;
        });
      }
      return; // native anchor navigation proceeds
    }
    e.preventDefault();
    openPanel(cat, d);
  });

  /* ---------- size disclosure + added state (delegated on root) ---------- */
  root.addEventListener('click', function (e) {
    const disc = e.target.closest('.ssd-chip.disc');
    if (disc) {
      const targetId = disc.getAttribute('aria-controls');
      const band = targetId ? document.getElementById(targetId) : null;
      if (band) {
        const open = band.classList.toggle('open');
        disc.classList.toggle('open', open);
        disc.setAttribute('aria-expanded', open ? 'true' : 'false');
      }
    }
  });

  // The cart add itself is handled by the site-wide form interceptor.
  // We only paint the chosen control once its form has submitted.
  root.addEventListener('submit', function (e) {
    // action$= : the forms post to routes.cart_add_url, which is /de-eu/cart/add
    // on a path-prefixed storefront; an exact match stopped lighting the chip there.
    const form = e.target.closest('form[action$="/cart/add"]');
    if (!form) return;
    const btn = e.submitter || form.querySelector('[type="submit"]');
    if (!btn) return;

    if (btn.classList.contains('ssd-szchip')) {
      // a chosen size stays lit — the choice is never erased
      if (!btn.classList.contains('done')) {
        btn.classList.add('done');
        const szEl = btn.querySelector('.ssd-sz');
        if (szEl && !szEl.querySelector('.ssd-szk')) {
          szEl.insertAdjacentHTML('afterbegin', '<span class="ssd-szk">✓</span>');
        }
      }
    } else if (btn.classList.contains('ssd-chip')) {
      if (btn.classList.contains('done')) return;
      const orig = btn.innerHTML;
      btn.classList.add('done');
      btn.innerHTML = '✓';
      setTimeout(function () { btn.classList.remove('done'); btn.innerHTML = orig; }, 1100);
    }
  });

  /* ---------- left-edge swipe to step back ---------- */
  let startX = null, startY = 0, dragging = false, decided = false, horiz = false, panelW = 0, dx = 0, current = null;
  function onDown(e) {
    if (!openPanelEl) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    const rect = openPanelEl.getBoundingClientRect();
    if (x - rect.left > 34) return;    // only from the left edge
    current = openPanelEl;
    startX = x; startY = y; panelW = rect.width; dragging = true; decided = false; horiz = false; dx = 0;
    current.style.transition = 'none';
  }
  function onMove(e) {
    if (!dragging || !current) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    const y = e.touches ? e.touches[0].clientY : e.clientY;
    dx = Math.max(0, x - startX);
    if (!decided) { decided = true; horiz = Math.abs(x - startX) > Math.abs(y - startY); }
    if (!horiz) return;
    current.style.transform = 'translateX(' + dx + 'px)';
    const p = Math.min(1, dx / panelW);
    level1.style.transition = 'none';
    level1.style.transform = 'translateX(' + (-28 + 28 * p) + '%)';
    if (e.cancelable) e.preventDefault();
  }
  function onUp() {
    // Reset the gesture flags before the early return. onDown claims the same
    // left-edge strip iOS Safari reserves for its own back gesture; when Safari
    // wins it fires touchcancel, not touchend. Without the touchcancel binding
    // below and this unconditional reset, `dragging` stayed true and the panel
    // kept `transform:translateX(..); transition:none` forever — visibly stuck
    // half-slid, and the next stray touchmove resumed the abandoned drag.
    var wasDragging = dragging, el = current, wasHoriz = horiz, movedBy = dx;
    dragging = false; decided = false; horiz = false; current = null; startX = null;
    if (!wasDragging || !el) return;
    el.style.transition = ''; level1.style.transition = '';
    if (wasHoriz && movedBy > panelW * 0.32) {
      el.style.transition = 'transform .3s cubic-bezier(0.32,0.72,0,1)';
      el.style.transform = 'translateX(100%)';
      level1.style.transform = '';
      root.classList.remove('is-pushed');
      openPanelEl = null;
      releaseLevel1();
      setTimeout(function () {
        el.style.transition = ''; el.style.transform = '';
        el.classList.remove('open'); el.setAttribute('aria-hidden', 'true');
      }, 300);
    } else {
      el.style.transform = ''; level1.style.transform = '';
    }
  }
  if (drawer) {
    drawer.addEventListener('touchstart', onDown, { passive: true });
    drawer.addEventListener('touchmove', onMove, { passive: false });
    drawer.addEventListener('touchend', onUp);
    // Safari fires touchcancel instead of touchend when it claims the left-edge
    // gesture; without this the drag never finished and the panel stuck.
    drawer.addEventListener('touchcancel', onUp);
    drawer.addEventListener('mousedown', onDown);
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
  }
})();

/* Ink settle — the drawer answers the first tap.
   PostHog (28 Jul): a visitor tapped "Zobacz wszystko" six times over 3.8s
   because the open drawer looked unchanged while the page was arriving.
   The tapped link accepts the press instantly (CSS :active costs no JS),
   commits visibly on click, dims the rest of the drawer, prefetches the
   destination at pointerdown, and swallows only DUPLICATE taps — never the
   first intent. bfcache returns reset the state via pageshow. */
(function () {
  var root = document.querySelector('.ssd-root');
  if (!root) return;
  var prefetched = {};

  root.addEventListener('pointerdown', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;
    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') return;
    if (sameDocument(a)) return; /* nothing to prefetch: this page is already here */
    if (prefetched[href]) return;
    prefetched[href] = 1;
    try {
      var l = document.createElement('link');
      l.rel = 'prefetch';
      l.href = href;
      document.head.appendChild(l);
    } catch (err) { /* prefetch is a bonus, never a blocker */ }
  }, true);

  /* "See all oils" carries a full path (/collections/all#oils) so it works
     from every page. Tapped ON /collections/all it is a fragment jump, not a
     page load: the browser scrolls the page behind the sheet and nothing
     else happens. This handler used to treat it as a departure — "Loading:
     See all oils", aria-busy, repeat taps swallowed for four seconds — while
     the sheet stayed open over a page that had already moved. On a slow
     phone that reads as the tab never loading (customer report, Sep 2026). */
  function sameDocument(a) {
    var u;
    try { u = new URL(a.href, location.href); } catch (err) { return false; }
    return !!u.hash && u.origin === location.origin &&
      u.pathname === location.pathname && u.search === location.search;
  }

  /* #PageRecede sits at scale(.965) while the sheet is open and spends half a
     second growing back (sacrasoul-drawer.css). A fragment landing measured
     during that transition is measured against a shrunken page, so the chapter
     the shopper asked for drifts down as the page expands, tens of pixels off.
     Align once more when the transform has actually finished.

     One alignment can ever be pending. It belongs to a single tap, and it is
     abandoned the moment that tap stops being what the shopper wants: they
     scroll or tap somewhere themselves, they press Back, they reopen the menu,
     or they ask for a different chapter. An alignment that survives any of
     those yanks the page out from under them. */
  var pendingAlign = null;

  function cancelAlign() {
    if (!pendingAlign) return;
    var p = pendingAlign;
    pendingAlign = null;
    if (p.shell && p.onEnd) p.shell.removeEventListener('transitionend', p.onEnd);
    clearTimeout(p.timer);
  }

  /* Any of these means the shopper has taken over. Registered once, and cheap:
     they do nothing at all unless an alignment is actually waiting. Note that
     `scroll` is NOT among them — our own scrollIntoView fires it. */
  ['pointerdown', 'touchstart', 'wheel', 'keydown', 'popstate'].forEach(function (ev) {
    window.addEventListener(ev, function () { cancelAlign(); }, { passive: true, capture: true });
  });

  function alignAfterRecede(hash) {
    cancelAlign();
    if (!hash || hash.charAt(0) !== '#') return;
    var shell = document.getElementById('PageRecede');
    var mine = { hash: hash, shell: shell, onEnd: null, timer: 0 };
    pendingAlign = mine;

    function align() {
      if (pendingAlign !== mine) return;   /* superseded or cancelled */
      cancelAlign();
      /* Only finish the alignment the current fragment still asks for... */
      if (location.hash !== hash) return;
      /* ...and only onto a page the shopper can see. Reopening the menu
         transitions #PageRecede back DOWN to scale(.965), which fires the same
         transitionend on the same element for the same property. Scrolling
         then would move a page hidden behind the sheet. */
      if (root.classList.contains('is-open')) return;
      var el = document.getElementById(hash.slice(1));
      if (el) el.scrollIntoView();
    }

    /* With no transition to wait for — desktop widths, reduced motion, a page
       already at rest — waiting 560 ms would be 560 ms of the shopper looking
       at the wrong place. Next frame is the whole wait. */
    var dur = shell ? parseFloat(getComputedStyle(shell).transitionDuration) || 0 : 0;
    if (!shell || !dur) { requestAnimationFrame(align); return; }

    mine.onEnd = function (ev) {
      if (ev.target === shell && ev.propertyName === 'transform') align();
    };
    shell.addEventListener('transitionend', mine.onEnd);
    /* transitionend does not fire for a transition that is interrupted or
       never starts, so the timer is the guarantee, not the plan. */
    mine.timer = setTimeout(align, Math.ceil(dur * 1000) + 60);
  }

  var committedHref = null;
  root.addEventListener('click', function (e) {
    var a = e.target && e.target.closest ? e.target.closest('a[href]') : null;
    if (!a) return;
    var href = a.getAttribute('href') || '';
    if (!href || href.charAt(0) === '#') return;
    /* A modified click opens a new tab or window: this page is not going
       anywhere, so the sheet must stay exactly as the shopper left it. */
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (sameDocument(a)) {
      var target = a.hash;
      var closeAndLand = function () {
        if (typeof root.__ssdClose === 'function') root.__ssdClose();
        /* closeDrawer restores the pre-open scroll position synchronously, and
           the fragment navigation (the link's default action) lands on the
           chapter. But it lands against a page still shrunk to scale(.965), so
           it settles about 70px short and would be corrected half a second
           later -- the shopper sees the chapter arrive, then jump.

           offsetTop is LAYOUT, and a transform does not change layout. So the
           final resting position is knowable right now, before the page has
           finished growing: scroll straight to it and the only movement left is
           the page expanding into place, which is the animation, not a
           correction. Same hash is not a navigation at all and needs this too. */
        requestAnimationFrame(function () {
          var el = target && document.getElementById(target.slice(1));
          if (!el) return;
          if (location.hash !== target) return;
          var margin = parseFloat(getComputedStyle(el).scrollMarginTop) || 0;
          var want = Math.max(0, el.offsetTop - margin);
          if (Math.abs(window.scrollY - want) > 1) window.scrollTo(0, want);
        });
        /* Kept as the guard, not the plan: if anything above was wrong about
           the geometry, this still puts the chapter where it belongs once the
           transform has actually finished. Normally a no-op. */
        alignAfterRecede(target);
      };
      /* This listener is CAPTURE (see the note at its tail: panel rows
         stopPropagation, so a bubble listener never saw See all at all), which
         means defaultPrevented is not yet the truth here — no handler further
         down has run. Several links inside the sheet carry a same-document
         href and are answered internally: a category doorway opens a panel
         instead of navigating, and it cancels the click to do it. Closing on
         those regardless shut the sheet and then opened a panel inside the
         closed sheet, so on /collections/all the tap looked like it did
         nothing. So every same-document decision waits for the end of dispatch,
         when the cancel has either happened or not. The browser performs the
         fragment navigation itself in between, so a link that really does
         navigate has already landed by the time this closes the sheet — and
         if a doorway's panel failed to hydrate and its href navigated for
         real, this still closes behind it. */
      setTimeout(function () { if (!e.defaultPrevented) closeAndLand(); }, 0);
      return;
    }
    /* Dedupe ONLY a repeat tap of the same committed link: the first intent is
       already navigating. Any other link stays functional. */
    if (committedHref && a.href === committedHref) { e.preventDefault(); return; }
    /* Decide AFTER every other handler has run: doorways and panel rows are
       anchors too, but the drawer handles them internally and prevents their
       default — those must never be marked as navigation. setTimeout(0) runs
       once the dispatch is complete, so defaultPrevented is the truth. */
    setTimeout(function () {
      if (e.defaultPrevented) return;
      committedHref = a.href;
      a.classList.add('ssd-commit');
      var drawer = root.querySelector('.ssd-drawer');
      if (drawer) { drawer.classList.add('ssd-nav-committed'); drawer.setAttribute('aria-busy', 'true'); }
      var s = root.querySelector('.ssd-nav-status');
      if (!s) {
        s = document.createElement('span');
        s.className = 'ssd-nav-status';
        s.setAttribute('role', 'status');
        s.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap';
        root.appendChild(s);
      }
      var L = (document.documentElement.lang || 'en').slice(0, 2);
      var M = { pl: '\u0141adowanie: ', cs: 'Na\u010d\u00edt\u00e1n\u00ed: ', de: 'Wird geladen: ', nl: 'Laden: ', en: 'Loading: ' };
      /* Announce only the link's name. aria-label wins, else the text,
         whitespace collapsed, first sentence, capped. */
      var label = a.getAttribute('aria-label') || (a.textContent || '');
      label = label.replace(/\u203a/g, ' ').replace(/\s+/g, ' ').trim().split('. ')[0].slice(0, 60);
      s.textContent = (M[L] || M.en) + label;
      /* Failsafe: if navigation is blocked or extremely slow, release the lock
         so the visitor is never stranded in a dead-feeling drawer. */
      setTimeout(function () {
        committedHref = null;
        a.classList.remove('ssd-commit');
        if (drawer) { drawer.classList.remove('ssd-nav-committed'); drawer.removeAttribute('aria-busy'); }
      }, 4000);
    }, 0);
  }, true); /* capture: panel rows stop propagation, so a bubble listener never
               saw the see-all click at all */

  window.addEventListener('pageshow', function () {
    committedHref = null;
    var a = root.querySelector('.ssd-commit');
    if (a) a.classList.remove('ssd-commit');
    var d = root.querySelector('.ssd-nav-committed');
    if (d) { d.classList.remove('ssd-nav-committed'); d.removeAttribute('aria-busy'); }
  });
})();
